/**
 * Client CSV feed configs — intake mapping as DATA, not code.
 *
 * Six clients email us inspection batches as CSV, each with its own column
 * layout. Rather than one parser per client, a ClientFeedConfig describes:
 *
 *   - how to RECOGNISE the feed (headerSignature: headers that must all be
 *     present — lets POST /api/admin/intake/csv auto-detect the client)
 *   - how to MAP feed columns onto InspectionJob fields
 *   - how to decide preConfirmed (fixed per client, or read from a column)
 *   - how to derive the inspection type when the feed has no explicit column
 *     (BTY→battery / PVD→pv case-id prefix rule)
 *
 * Two real configs ship now (derived from the demo/trial data — headers are
 * plain data, so adjusting them when a client tweaks their export is a
 * one-line change). The remaining feeds get added as configs when sample
 * files arrive. `generic` is the fallback for hand-assembled CSVs using our
 * canonical column names.
 */
import { z } from 'zod';
import { InspectionTypeSchema } from './cer-portal.js';
import { JobStatusSchema } from './jobs.js';
/**
 * Feed column → case field mapping. Every value is a HEADER NAME in the
 * client's CSV. Only `accreditationCode` is mandatory — the mapper fills what's there
 * and leaves the rest undefined.
 */
export const ClientFeedColumnsSchema = z.object({
    accreditationCode: z.string().min(1),
    inspectionType: z.string().optional(),
    relatedCaseId: z.string().optional(),
    /**
     * A second accreditation code sitting on the same row as
     * `accreditationCode`. The CER portal export does this for a combined
     * PV+battery allocation: `Installation_Accreditation_Code` is the PVD job
     * and `Battery_Accreditation_Code` is the BTY job. It is not a pointer at
     * an existing job — the battery code has to become its own job, or the
     * same-site combo detector never sees a partner to pair.
     */
    batteryAccreditationCode: z.string().optional(),
    sourceRef: z.string().optional(),
    serviceM8JobNumber: z.string().optional(),
    tranche: z.string().optional(),
    area: z.string().optional(),
    systemSizeKw: z.string().optional(),
    moduleMake: z.string().optional(),
    moduleModel: z.string().optional(),
    moduleQuantity: z.string().optional(),
    inverterMake: z.string().optional(),
    inverterModel: z.string().optional(),
    /**
     * Dedicated battery columns. Feeds that carry these are preferred over the
     * `module*` columns on battery rows: CER exports reuse `SGU_Brand`/`SGU_Model`
     * for whatever small generation unit the row is about, so on a BTY row those
     * describe the battery — but the same export ALSO ships explicit
     * `SGU_Battery_*` columns, and those are the ones that stay right when a row
     * carries both a PV array and a battery.
     */
    batteryMake: z.string().optional(),
    batteryModel: z.string().optional(),
    /** Product family, e.g. "Lynx D G2" — distinct from the model number. */
    batterySeries: z.string().optional(),
    /** Legal manufacturer, e.g. "GoodWe Technologies Co Ltd". */
    batteryManufacturer: z.string().optional(),
    installationDate: z.string().optional(),
    systemType: z.string().optional(),
    batteryInstallationType: z.string().optional(),
    addressLine1: z.string().optional(),
    addressSuburb: z.string().optional(),
    addressState: z.string().optional(),
    addressPostcode: z.string().optional(),
    ownerFirstName: z.string().optional(),
    ownerLastName: z.string().optional(),
    /**
     * One column holding the whole name, for feeds that never split it. Read
     * only when the first/last pair is absent. ITP writes "Beil, Michael"; the
     * splitter reads the comma when there is one and falls back to
     * `fullNameOrder` when there is not.
     */
    ownerFullName: z.string().optional(),
    ownerEmail: z.string().optional(),
    ownerPhone: z.string().optional(),
    /**
     * Second phone column, read only when `ownerPhone` is blank or "Unknown".
     * The CER export splits mobile and landline, and populates whichever the
     * retailer captured — reading one column alone drops the number entirely on
     * most rows, which then leaves the ServiceM8 site contact unreachable.
     */
    ownerPhoneAlt: z.string().optional(),
    /**
     * Original installing electrician and system designer. Both prefill the
     * cover sheet and ride onto the ServiceM8 job as a reference note, so the
     * office can reach whoever did the work without going back to the client.
     */
    installerFirstName: z.string().optional(),
    installerLastName: z.string().optional(),
    /** Single-column variant, same rule as `ownerFullName`. */
    installerFullName: z.string().optional(),
    /** Installer's CEC accreditation code, e.g. "S3244154". */
    installerAccreditationCode: z.string().optional(),
    installerEmail: z.string().optional(),
    installerMobile: z.string().optional(),
    installerPhone: z.string().optional(),
    installerAddressLine1: z.string().optional(),
    installerAddressSuburb: z.string().optional(),
    installerAddressState: z.string().optional(),
    installerAddressPostcode: z.string().optional(),
    designerFirstName: z.string().optional(),
    designerLastName: z.string().optional(),
    /** Single-column variant, same rule as `ownerFullName`. */
    designerFullName: z.string().optional(),
    designerAccreditationCode: z.string().optional(),
    designerEmail: z.string().optional(),
    designerMobile: z.string().optional(),
    designerPhone: z.string().optional(),
    designerAddressLine1: z.string().optional(),
    designerAddressSuburb: z.string().optional(),
    designerAddressState: z.string().optional(),
    designerAddressPostcode: z.string().optional(),
    /**
     * Free text about the installation itself — scope of work, what was kept or
     * removed, battery master serials. Reaches the inspector as part of the
     * ServiceM8 booking note.
     */
    siteNotes: z.string().optional(),
    /**
     * The client's own live status for the row, in their vocabulary (e.g.
     * "Pending Booking" / "Rejected"). Read only by feeds whose sheet is worked
     * outside this tool between imports; mapped onto JobStatus by
     * `clientStatusMap` rather than compared directly.
     */
    clientStatus: z.string().optional(),
    /**
     * Date the homeowner approved the inspection. Presence — not the value — is
     * what a `whenPresent` preConfirmed rule reads.
     */
    ownerApprovalDate: z.string().optional(),
    /**
     * Homeowner's emailed reply to the client's approach letter. Scheduling
     * constraints live here ("we have two dogs, let me know when you're
     * coming"), so it rides to the top of the ServiceM8 booking note.
     */
    emailNotes: z.string().optional(),
    /**
     * What the client's office recorded after phoning the homeowner. Rarer than
     * the emailed reply — 3 of 300 rows in Kerfoot's T48A — but higher stakes
     * per row: two of those three are outright refusals, and the third is a
     * verbal confirmation. Sits beside `emailNotes` in the booking note.
     */
    callNotes: z.string().optional(),
    /**
     * Extra contact or access detail the client holds outside the owner's phone
     * field — a second number under another name, an availability window, a
     * battery at a different address. Free text; joins the notes at the top of
     * the booking note rather than being parsed into the owner record.
     */
    contactNotes: z.string().optional(),
    /** The client's own message to the inspector. */
    inspectorNotes: z.string().optional(),
});
/**
 * preConfirmed policy: a boolean when the client's process is fixed (e.g.
 * they always ring the homeowner before sending us the batch), or a column
 * read where the feed carries a per-row flag. Column values are compared
 * case-insensitively against `truthyValues`.
 */
export const PreConfirmedRuleSchema = z.union([
    z.boolean(),
    z.object({
        column: z.string().min(1),
        truthyValues: z.array(z.string().min(1)).min(1),
    }),
    /**
     * Confirmed when the column carries ANY value. For date columns — Kerfoot's
     * working sheet records an "Owner Approval Date" rather than a yes/no flag,
     * and enumerating truthy dates is not possible.
     */
    z.object({
        column: z.string().min(1),
        whenPresent: z.literal(true),
    }),
]);
/**
 * The client's status vocabulary → ours, for feeds whose sheet keeps moving
 * after we import it. Keys are compared lower-cased and trimmed; an
 * unrecognised value maps to nothing and leaves our status untouched, so a
 * word the client invents next month is inert rather than wrong.
 */
export const ClientStatusMapSchema = z.record(z.string().min(1), JobStatusSchema);
/**
 * Values a feed carries once for the whole workbook rather than per row.
 *
 * Kerfoot's working sheet has neither the allocation round nor the tranche
 * name — both live only on the CER export sheet beside it, identical on all
 * 300 rows. Reading them from a sibling sheet keeps the import driven by the
 * live sheet without resurrecting a row-level join between the two.
 */
export const WorkbookConstantsSchema = z.object({
    sourceRef: z.string().min(1).optional(),
    tranche: z.string().min(1).optional(),
});
export const ClientFeedConfigSchema = z.object({
    /** Stable parse-rule id. Jobs reference the owning Client, never this id. */
    feedConfigId: z.string().min(1),
    /** Admin-only parse-rule label. */
    label: z.string().min(1),
    match: z.object({
        /** Headers that must ALL be present (trimmed, case-insensitive) to auto-detect this feed. */
        headerSignature: z.array(z.string().min(1)).min(1),
    }),
    columns: ClientFeedColumnsSchema,
    preConfirmed: PreConfirmedRuleSchema,
    /** When set, derive inspectionType from the accreditation-code prefix. */
    inspectionTypeRule: z.object({ fromAccreditationCodePrefix: z.literal(true) }).optional(),
    /**
     * How to read a `*FullName` column that carries no comma. A value WITH a
     * comma is always read as "Surname, Given" — that punctuation is unambiguous
     * and no feed uses it any other way — so this only decides the fallback.
     */
    fullNameOrder: z.enum(['given-first', 'surname-first']).optional(),
    /**
     * Date layout in this feed's date columns. `iso` covers both real date cells
     * (XLSX hands those over already normalised) and text that is already
     * ISO-shaped. `dmy` is for feeds whose dates arrive as TEXT in Australian
     * order — ITP writes "21/05/2026", which stored verbatim would sort wrong on
     * the dashboard and read as 21 May or 5 Nov depending on who is looking.
     */
    dateFormat: z.enum(['iso', 'dmy']).optional(),
    /**
     * Fill the address state from the postcode. For feeds that carry no usable
     * state column — ITP's is headed "State" but holds their postal area.
     */
    deriveStateFromPostcode: z.boolean().optional(),
    /**
     * Read the battery install type from whether a partner code is present,
     * rather than from a type column.
     *
     * ITP's `Associated PVD` does double duty: a PVD code means the battery went
     * in alongside that array (concurrent), and the literal word "Retrofit" means
     * it did not (subsequent). One column, both facts, and neither expressible as
     * a plain `batteryInstallationType` mapping.
     */
    batteryInstallationTypeRule: z
        .object({ concurrentWhenRelatedCasePresent: z.literal(true) })
        .optional(),
    /** Fallback type when neither a column nor the prefix rule resolves it. */
    defaultInspectionType: InspectionTypeSchema.optional(),
    /**
     * Present on feeds that are re-imported to pick up the client's own edits.
     * Its presence is what marks a feed as re-importable: without it an upload
     * of rows we already hold changes nothing.
     */
    clientStatusMap: ClientStatusMapSchema.optional(),
    /** Columns to read once from a sibling worksheet rather than per row. */
    workbookConstants: WorkbookConstantsSchema.optional(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * Shipped configs
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Feed A — battery program batches. This client confirms the inspection with
 * the homeowner before sending the batch, so every row is pre-confirmed.
 * Header names derived from the demo batch backing the M2 seed data.
 */
const FEED_CLIENT_A = {
    feedConfigId: 'client-a',
    label: 'Client A — battery program (pre-confirmed)',
    match: {
        headerSignature: ['Accreditation Code', 'Owner First Name', 'Owner Last Name'],
    },
    columns: {
        accreditationCode: 'Accreditation Code',
        relatedCaseId: 'Related Accreditation Code',
        sourceRef: 'Client Ref',
        serviceM8JobNumber: 'ServiceM8 Job',
        installationDate: 'Installation Date',
        systemType: 'System Type',
        batteryInstallationType: 'Battery Installation Type',
        addressLine1: 'Address',
        addressSuburb: 'Suburb',
        addressState: 'State',
        addressPostcode: 'Postcode',
        ownerFirstName: 'Owner First Name',
        ownerLastName: 'Owner Last Name',
        ownerEmail: 'Owner Email',
        ownerPhone: 'Owner Phone',
    },
    preConfirmed: true,
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
};
/**
 * Feed B — mixed PV/battery batches with a per-row confirmation flag; we
 * chase the homeowner when the flag is blank/no.
 */
const FEED_CLIENT_B = {
    feedConfigId: 'client-b',
    label: 'Client B — mixed feed (per-row confirmation flag)',
    match: {
        headerSignature: ['Job ID', 'Surname', 'Homeowner Confirmed'],
    },
    columns: {
        accreditationCode: 'Job ID',
        sourceRef: 'Reference',
        installationDate: 'Install Date',
        systemType: 'Type',
        addressLine1: 'Street Address',
        addressSuburb: 'Suburb',
        addressState: 'State',
        addressPostcode: 'Postcode',
        ownerFirstName: 'First Name',
        ownerLastName: 'Surname',
        ownerEmail: 'Email',
        ownerPhone: 'Phone',
    },
    preConfirmed: { column: 'Homeowner Confirmed', truthyValues: ['yes', 'y', 'true', 'confirmed'] },
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
};
/**
 * CER portal export — the Exported_Inspections worksheet, as the portal emits
 * it. A FORMAT, not a client: the portal hands every accredited inspection
 * company the same columns, so more than one of our clients sends this layout.
 *
 * That is why nothing here is named for a client, and why the signature below
 * cannot resolve one. `Installation_Company_Name` is the INSTALLER's business,
 * not the client who allocated us the work, and no other column carries it —
 * so a file in this layout tells us what it is but never whose it is. When two
 * or more active Clients claim this config, the intake route stops with
 * `client-resolution-required` and the operator picks. That stop is the point:
 * this config was previously called `kerfoot-cer-export` and claimed by
 * Kerfoot alone, so a TechSafe export auto-detected straight into Kerfoot's
 * book — 109 jobs, silently, with every other field mapped correctly.
 */
const FEED_CER_PORTAL_EXPORT = {
    feedConfigId: 'cer-portal-export',
    label: 'CER portal export (Exported_Inspections worksheet)',
    match: {
        headerSignature: [
            'Installation_Accreditation_Code',
            'Installation_Company_Name',
            'Tranche_Name',
            'Allocation_ID',
        ],
    },
    columns: {
        accreditationCode: 'Installation_Accreditation_Code',
        /**
         * The battery half of a combined allocation line. Populated together with
         * a PVD `Installation_Accreditation_Code` on the rows TechSafe (and the
         * same portal layout) uses for a PV+battery visit — 25 of 106 in one
         * file. A blank cell leaves the row as the single job it already was.
         * A row whose installation code is itself blank is still skipped; this
         * column is not read as a fallback accreditation code.
         */
        batteryAccreditationCode: 'Battery_Accreditation_Code',
        /**
         * The PVD code travelling with a concurrent BTY install (and vice versa).
         * Populated only on `Battery_Installation_Type: Concurrent` rows.
         */
        relatedCaseId: 'Existing_Accreditation_Code',
        sourceRef: 'Allocation_ID',
        tranche: 'Tranche_Name',
        installationDate: 'Small_Unit_Installed_date',
        systemType: 'Fuel_Source',
        systemSizeKw: 'SGU_Rated_Output_in_KW',
        moduleMake: 'SGU_Brand',
        moduleModel: 'SGU_Model',
        moduleQuantity: 'SGU_Panels_Quantity',
        inverterMake: 'Inverter_Brand',
        inverterModel: 'Inverter_Model',
        batteryMake: 'SGU_Battery_Brand',
        batteryModel: 'SGU_Battery_Model',
        batterySeries: 'SGU_Battery_Series',
        batteryManufacturer: 'Battery_Manufacturer',
        batteryInstallationType: 'Battery_Installation_Type',
        area: 'Postal_Area_Name',
        addressLine1: 'Small_Unit_Installation_Street_Address_Full',
        addressSuburb: 'Small_Unit_Installation_City',
        addressState: 'Small_Unit_Installation_State',
        addressPostcode: 'Small_Unit_Installation_Postcode',
        ownerFirstName: 'Small_Unit_Owner_Firstname',
        ownerLastName: 'Small_Unit_Owner_Surname',
        ownerEmail: 'Small_Unit_Owner_Email_Address',
        // Mobile is "Unknown" on most rows; the landline column carries the number.
        ownerPhone: 'Small_Unit_Owner_Mobile_Number',
        ownerPhoneAlt: 'Small_Unit_Owner_Phone_Number',
        installerFirstName: 'Small_Unit_Installer_Firstname',
        installerLastName: 'Small_Unit_Installer_Surname',
        installerAccreditationCode: 'Small_Unit_Installer_CEC_Accreditation_Code',
        installerEmail: 'Small_Unit_Installer_Email_Address',
        // Same split as the owner: mobile is populated on ~1 row in 3, the
        // landline on all of them.
        installerMobile: 'Small_Unit_Installer_Mobile_Number',
        installerPhone: 'Small_Unit_Installer_Phone_Number',
        installerAddressLine1: 'Small_Unit_Installer_Street_Address_Full',
        installerAddressSuburb: 'Small_Unit_Installer_City',
        installerAddressState: 'Small_Unit_Installer_State',
        installerAddressPostcode: 'Small_Unit_Installer_Postcode',
        designerFirstName: 'SGU_Designer_Firstname',
        designerLastName: 'SGU_Designer_Surname',
        designerAccreditationCode: 'SGU_Designer_CEC_Accreditation_Code',
        designerEmail: 'SGU_Designer_Email_Address',
        designerMobile: 'SGU_Designer_Mobile_Number',
        designerPhone: 'SGU_Designer_Phone_Number',
        designerAddressLine1: 'SGU_Designer_Street_Address_Full',
        designerAddressSuburb: 'SGU_Designer_City',
        designerAddressState: 'SGU_Designer_State',
        designerAddressPostcode: 'SGU_Designer_Postcode',
        // Despite the "address" in the column name, this carries installation
        // detail: scope of work and the battery master serial(s).
        siteNotes: 'Small_Unit_Installation_Additional_Address_Information',
    },
    preConfirmed: false,
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
};
/**
 * Kerfoot's "Tranche Management" sheet — the front page of the workbook they
 * send, and the one they keep working after they send it.
 *
 * Prefer this over `cer-portal-export` for anything Kerfoot. Both sheets ship
 * in the same file and hold the same 300 jobs (1:1 on accreditation code, zero
 * orphans either way in T48A), but the export sheet's status is frozen at
 * allocation time. Importing it ingests Kerfoot's rejected rows as live work —
 * 14 of 300 in T48A — and carries none of the homeowner contact history.
 *
 * What only this sheet has: live `Status`, `Owner Approval Date`, the
 * homeowner's emailed reply, solar panel quantity, and the site free-text.
 * What it lacks: `Allocation_ID` and `Tranche_Name`, both workbook-constants
 * read from the export sheet via `workbookConstants`.
 *
 * Mobile/phone are SWAPPED relative to the export sheet — the number Kerfoot
 * files under "Owner Phone Number" there arrives under "Owner Mobile Number"
 * here — so the column order below is deliberate, not copied.
 */
const FEED_KERFOOT_TRANCHE = {
    feedConfigId: 'kerfoot-tranche-management',
    label: 'Kerfoot — tranche management sheet (re-importable)',
    match: {
        // "Status" and "PVD/BTY" are both absent from the export sheet beside it,
        // so this signature cannot match the wrong worksheet of the same workbook.
        headerSignature: ['PVD/BTY', 'Status', 'Additional Site Information', 'Area Name'],
    },
    columns: {
        accreditationCode: 'PVD/BTY',
        /** The concurrent partner's code — populated on 76 of 300 rows in T48A. */
        relatedCaseId: 'Associated Install (Concurrent Only)',
        clientStatus: 'Status',
        ownerApprovalDate: 'Owner Approval Date',
        emailNotes: 'Email Notes',
        callNotes: 'Call notes',
        area: 'Area Name',
        systemSizeKw: 'kW Rating',
        installationDate: 'Installed Date',
        systemType: 'Fuel Source',
        moduleMake: 'Brand',
        moduleModel: 'Model',
        // Non-zero on exactly the 103 PVD rows: on a combo the count reaches the
        // battery half through the partner merge, not from its own row.
        moduleQuantity: 'Solar Panel Quantity',
        inverterMake: 'Inverter Brand',
        inverterModel: 'Inverter Model',
        batteryMake: 'SGU_Battery_Brand',
        batteryModel: 'SGU_Battery_Model',
        batterySeries: 'SGU_Battery_Series',
        batteryManufacturer: 'Battery_Manufacturer',
        batteryInstallationType: 'Battery_Installation_Type',
        addressLine1: 'Address',
        addressSuburb: 'Suburb',
        addressState: 'State',
        addressPostcode: 'Postcode',
        ownerFirstName: 'Owner Firstname',
        ownerLastName: 'Owner Surname',
        ownerEmail: 'Owner Email Address',
        ownerPhone: 'Owner Mobile Number',
        ownerPhoneAlt: 'Owner Phone Number',
        installerFirstName: 'Installer Firstname',
        installerLastName: 'Installer Surname',
        installerAccreditationCode: 'Installer Accreditation Code',
        installerEmail: 'Installer Email Address',
        installerMobile: 'Installer Mobile Number',
        installerPhone: 'Installer Phone Number',
        installerAddressLine1: 'Installer Address',
        installerAddressSuburb: 'Installer City',
        installerAddressState: 'Installer State',
        installerAddressPostcode: 'Installer Post Code',
        designerFirstName: 'Designer Firstname',
        designerLastName: 'Designer Surname',
        designerAccreditationCode: 'Designer Accreditation',
        designerEmail: 'Designer Email',
        designerMobile: 'Designer Mobile',
        // Kerfoot's second designer column is a duplicate of the mobile, not a
        // landline; it is read as the phone so the party card matches the export.
        designerPhone: 'Designer Mobile2',
        designerAddressLine1: 'Designer Address',
        designerAddressSuburb: 'Designer Suburb',
        designerAddressState: 'Designer State',
        designerAddressPostcode: 'Designer Postcode',
        siteNotes: 'Additional Site Information',
    },
    /**
     * Kerfoot stamps the date the homeowner approved rather than a flag — 49 of
     * 300 rows in T48A. Absent means we still have to make the call, which is
     * exactly what `needs-confirmation` means.
     */
    preConfirmed: { column: 'Owner Approval Date', whenPresent: true },
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
    /**
     * Kerfoot's wording, verbatim from the sheet's Stats tab. Statuses past the
     * booking stage deliberately map no further than `confirmed`: their sheet
     * knows an inspection was arranged, but not by whom or when, and ServiceM8
     * is what actually books an inspector.
     */
    clientStatusMap: {
        'pending booking': 'needs-confirmation',
        'inspection approved': 'confirmed',
        'inspection booked': 'confirmed',
        rejected: 'cancelled',
    },
    workbookConstants: { sourceRef: 'Allocation_ID', tranche: 'Tranche_Name' },
};
/**
 * GSES allocation sheet.
 *
 * Same CER lineage as the Kerfoot export — `Allocation_ID`, `Tranche_Name`,
 * the `Small_Unit_*` address and owner block — but restructured, so it misses
 * `cer-portal-export` by one header (`Inspection_Company_Name` where Kerfoot
 * writes `Installation_Company_Name`) and would drop 30 of 48 mapped columns
 * if it were forced through it anyway. Three structural differences:
 *
 *   - Hardware is split PV/battery (`PV Brand` / `Battery Brand`) instead of
 *     one `SGU_*` pair reused per row type. The `SGU_*` columns are still
 *     here, but they duplicate the PV half and are empty on a Retrofit row.
 *   - Installer and designer are split the same way. Only the BATTERY set is
 *     read: it is populated on every row of an allocation whose rows all carry
 *     a battery (22/22 here, against 6/22 for the PV set), and on the combo
 *     rows the two sets name the same person and accreditation code. A future
 *     PV-only GSES allocation would need the `PV Installer *` columns instead,
 *     which this config cannot express — it would import with no installer
 *     rather than a wrong one.
 *   - Both codes ride on their own columns. `Installation_Accreditation_Code`
 *     is the row's own code. `BTY_Code` is the partner on a combo row and a
 *     copy of the row's own code on a Retrofit row. The same header is mapped
 *     as `relatedCaseId` and as `batteryAccreditationCode`, so a combo row
 *     emits the companion battery job the CER portal export emits from
 *     `Battery_Accreditation_Code`. A Retrofit self-copy is discarded rather
 *     than stored as a self-reference, and it does not emit a second job.
 */
const FEED_GSES = {
    feedConfigId: 'gses-allocation',
    label: 'GSES — allocation sheet',
    match: {
        /**
         * All four are absent from both Kerfoot sheets, and GSES matches neither
         * of theirs, so the three CER-derived layouts stay mutually exclusive.
         */
        headerSignature: ['Inspection_Company_Name', 'PVD_Code', 'BTY_Code', 'Retrofit or PV+BESS?'],
    },
    columns: {
        accreditationCode: 'Installation_Accreditation_Code',
        /**
         * The combo partner, and the battery job emitted beside a solar row.
         * Equal to the row's own code on Retrofit rows — that self-copy is not
         * stored, and no companion is created. See `batteryAccreditationCode`.
         */
        relatedCaseId: 'BTY_Code',
        /**
         * Same cell as `relatedCaseId`. The portal-export companion path reads
         * this column; without it a GSES combo row stored `BTY_Code` only as a
         * pointer and never created the battery job.
         */
        batteryAccreditationCode: 'BTY_Code',
        sourceRef: 'Allocation_ID',
        tranche: 'Tranche_Name',
        area: 'Postal_Area_Name',
        installationDate: 'Small_Unit_Installed_Date',
        systemType: 'Fuel_Source',
        systemSizeKw: 'SGU_Rated_Output_in_kW',
        /**
         * The PV half explicitly, not the `SGU_*` pair. On a combo row the two are
         * identical; on a Retrofit row the `SGU_*` pair is empty and the battery
         * lives in the `Battery *` columns below — so the split columns say what
         * the row holds without the "SGU means whatever this row is about" rule
         * the Kerfoot export needs.
         */
        moduleMake: 'PV Brand',
        moduleModel: 'PV Model',
        moduleQuantity: 'PV Panel Quantity',
        inverterMake: 'PV Inverter Brand',
        inverterModel: 'PV Inverter Model',
        batteryMake: 'Battery Brand',
        batteryModel: 'Battery Model',
        /** "Retrofit" / "PV+BESS" — the row-type column, not a battery column. */
        batteryInstallationType: 'Retrofit or PV+BESS?',
        addressLine1: 'Small_Unit_Installation_Street_Address_Full',
        addressSuburb: 'Small_Unit_Installation_City',
        addressState: 'Small_Unit_Installation_State',
        addressPostcode: 'Small_Unit_Installation_Postcode',
        ownerFirstName: 'Small_Unit_Owner_Firstname',
        ownerLastName: 'Small_Unit_Owner_Surname',
        // Not `Small_Unit_Owner_Email_Address`: GSES drops the prefix on this one
        // column alone. Populated on every row, unlike the mobile (3 of 22).
        ownerEmail: 'Owner_Email_Address',
        ownerPhone: 'Small_Unit_Owner_Mobile_Number',
        installerFirstName: 'Battery Installer First Name',
        installerLastName: 'Battery Installer Surname',
        installerAccreditationCode: 'Battery Installer SAA Accreditation Code',
        installerEmail: 'Battery Installer Email',
        installerMobile: 'Battery Installer Mobile Number',
        installerPhone: 'Battery Installer Phone Number',
        designerFirstName: 'Battery Designer First Name',
        designerLastName: 'Battery Designer Last Name',
        designerAccreditationCode: 'Battery Designer SAA Accreditation Code',
        designerEmail: 'Battery Designer Email',
        // Same column as the Kerfoot export, same ". Master serial is …" shape.
        siteNotes: 'Small_Unit_Installation_Additional_Address_Information',
        contactNotes: 'Additional Contact Details email/mobile',
        inspectorNotes: 'Other Comments to Inspector',
    },
    /**
     * GSES ships no approval column. `Check Address` is an address QA flag, not
     * homeowner contact, so every row lands as `needs-confirmation`.
     */
    preConfirmed: false,
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
};
/**
 * ITP Renewables allocation sheet.
 *
 * A third CER-derived dialect: the same `Small Unit *` vocabulary as the portal
 * export, but with SPACES where the export uses underscores, so it matches
 * neither of the other two on a single header. Four structural differences,
 * each of which needed a rule rather than a column mapping:
 *
 *   - Names arrive whole, one column each ("Beil, Michael" / "Solman, Jason"),
 *     never split into first and surname.
 *   - `Small Unit Installation State` is NOT the state. It holds ITP's postal
 *     area — "Northern QLD", "Greater Sydney" — so it maps to `area`, and the
 *     real state is derived from the postcode.
 *   - `Associated PVD` carries the partner PVD code, "Retrofit", or
 *     "Concurrent PV", making it both the related case and the battery
 *     install type.
 *   - Dates are TEXT in "21/05/2026" order, not date cells.
 *
 * Three of their columns are duplicated under the same header — the sheet keeps
 * a `TRIM(PROPER(…))` formula beside each raw value. The parser keeps the last
 * occurrence, but nothing here depends on which one wins: the name splitter
 * reads either order, and `titleCaseIfShouting` fixes the shouted address.
 *
 * No hardware, tranche, allocation or status columns — this sheet is an
 * allocation list, not a working sheet, so there is nothing to re-import.
 */
const FEED_ITP = {
    feedConfigId: 'itp-allocation',
    label: 'ITP Renewables — allocation sheet',
    match: {
        /**
         * `Associated PVD` and `Inspection company` are absent from every other
         * layout, and the two `Small Unit *` headers are spelled with spaces only
         * here, so this cannot collide with the portal export or the GSES sheet.
         */
        headerSignature: [
            'Small Unit Accreditation Code',
            'Associated PVD',
            'Inspection company',
            'Small Unit Installed Date',
        ],
    },
    columns: {
        accreditationCode: 'Small Unit Accreditation Code',
        /**
         * Three vocabularies in one column: a PVD code (the array this battery
         * went in with), "Retrofit" (it did not), or "Concurrent PV" (this row IS
         * the array half — 21 of 73). Only a real code is kept as the partner;
         * both words are dropped rather than stored as a case id.
         */
        relatedCaseId: 'Associated PVD',
        installationDate: 'Small Unit Installed Date',
        // Their "State" column, read as what it actually contains.
        area: 'Small Unit Installation State',
        addressLine1: 'Small Unit Installation Street Address Full',
        addressSuburb: 'Small Unit Installation City',
        addressPostcode: 'Small Unit Installation Postcode',
        ownerFullName: 'Small Unit Owner Full Name',
        ownerEmail: 'Small Unit Owner Email Address',
        // Mobile is populated on 5 of 73 rows; the landline on all of them.
        ownerPhone: 'Small Unit Owner Mobile Number',
        ownerPhoneAlt: 'Small Unit Owner Phone Number',
        installerFullName: 'Small Unit Installer Full Name',
        installerAccreditationCode: 'Small Unit Installer CEC Accreditation Code',
        installerEmail: 'Small Unit Installer Email Address',
        installerMobile: 'Small Unit Installer Mobile Number',
        installerPhone: 'Small Unit Installer Phone Number',
        designerFullName: 'SGU Designer Full Name',
        designerAccreditationCode: 'SGU Designer CEC Accreditation Code',
        designerEmail: 'SGU Designer Email Address',
        designerMobile: 'SGU Designer Mobile Number',
        designerPhone: 'SGU Designer Phone Number',
    },
    /** No approval column, so every row lands as `needs-confirmation`. */
    preConfirmed: false,
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
    fullNameOrder: 'given-first',
    dateFormat: 'dmy',
    deriveStateFromPostcode: true,
    batteryInstallationTypeRule: { concurrentWhenRelatedCasePresent: true },
};
/**
 * Smart Energy Council's worked allocation sheet.
 *
 * One row represents one CER accreditation code. Concurrent PV+battery sites
 * therefore appear twice, while `Retrofit / Concurrent` carries the partner
 * PVD code on the battery row and the word "Concurrent" on the PV row. The
 * code-bearing half is enough for the intake combo detector to link both rows.
 *
 * The sheet is sent after homeowner acceptance and already carries a live
 * office status, inspection date and inspector. ServiceM8 remains authoritative
 * for the actual booking/assignment; the status mapping below deliberately
 * stops at `confirmed` so a spreadsheet cannot manufacture field submissions.
 */
const FEED_SMART_ENERGY = {
    feedConfigId: 'smart-energy-allocation',
    label: 'Smart Energy Council — allocation sheet',
    match: {
        headerSignature: ['PVD/ BTY Code', 'Retrofit / Concurrent', 'AuSES Notes', 'Account_Name'],
    },
    columns: {
        accreditationCode: 'PVD/ BTY Code',
        relatedCaseId: 'Retrofit / Concurrent',
        clientStatus: 'Status',
        emailNotes: 'AuSES Notes',
        inspectorNotes: 'Inspector Notes',
        installationDate: 'Small_Unit_Installed_date',
        systemSizeKw: 'SGU_Rated_Output_in_KW',
        moduleMake: 'SGU_Brand',
        moduleModel: 'SGU_Model',
        moduleQuantity: 'SGU_Panels_Quantity',
        inverterMake: 'Inverter_Brand',
        inverterModel: 'Inverter_Model',
        addressLine1: 'Street Address',
        addressSuburb: 'City',
        addressState: 'State',
        addressPostcode: 'Postcode',
        ownerFirstName: 'First Name',
        ownerLastName: 'Surname',
        ownerEmail: 'Email Address',
        ownerPhone: 'Mobile Contact',
        installerFirstName: 'Installer_Firstname',
        installerLastName: 'Installer_Surname',
        installerAccreditationCode: 'Installer_CEC_Accreditation_Code',
        installerPhone: 'Installer Phone Number',
        designerFirstName: 'Designer_Firstname',
        designerLastName: 'Designer_Surname',
        designerAccreditationCode: 'Designer_CEC_Accreditation_Code',
        siteNotes: 'Additional_Address_Information',
    },
    preConfirmed: true,
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
    batteryInstallationTypeRule: { concurrentWhenRelatedCasePresent: true },
    clientStatusMap: {
        booked: 'confirmed',
        inspected: 'confirmed',
        'report ready': 'confirmed',
    },
};
/**
 * Generic fallback — our canonical column names, for hand-assembled CSVs and
 * for clients whose config hasn't been captured yet (export → rename headers
 * → upload).
 */
const FEED_GENERIC = {
    feedConfigId: 'generic',
    label: 'Generic (canonical column names)',
    match: {
        headerSignature: ['accreditationCode'],
    },
    columns: {
        accreditationCode: 'accreditationCode',
        inspectionType: 'inspectionType',
        relatedCaseId: 'relatedCaseId',
        sourceRef: 'sourceRef',
        serviceM8JobNumber: 'serviceM8JobNumber',
        installationDate: 'installationDate',
        systemType: 'systemType',
        batteryInstallationType: 'batteryInstallationType',
        moduleMake: 'moduleMake',
        moduleModel: 'moduleModel',
        moduleQuantity: 'moduleQuantity',
        inverterMake: 'inverterMake',
        inverterModel: 'inverterModel',
        addressLine1: 'addressLine1',
        addressSuburb: 'suburb',
        addressState: 'state',
        addressPostcode: 'postcode',
        ownerFirstName: 'ownerFirstName',
        ownerLastName: 'ownerLastName',
        ownerEmail: 'ownerEmail',
        ownerPhone: 'ownerPhone',
    },
    preConfirmed: { column: 'preConfirmed', truthyValues: ['yes', 'y', 'true', '1'] },
    inspectionTypeRule: { fromAccreditationCodePrefix: true },
};
/** All shipped feed configs. Detection order: most specific signature first. */
export const CLIENT_FEED_CONFIGS = [
    FEED_KERFOOT_TRANCHE,
    FEED_CER_PORTAL_EXPORT,
    FEED_GSES,
    FEED_ITP,
    FEED_SMART_ENERGY,
    FEED_CLIENT_A,
    FEED_CLIENT_B,
    FEED_GENERIC,
];
/**
 * Whether re-uploading this feed should update the jobs it already created.
 *
 * True only for feeds that carry the client's live status, because that is the
 * evidence a later upload is a fresher view of the same row rather than a
 * duplicate. Everything else keeps the original behaviour: known rows are
 * counted as duplicates and left alone.
 */
export function isReimportable(config) {
    return config.clientStatusMap !== undefined && config.columns.clientStatus !== undefined;
}
/** Map a client's own status word onto ours. Undefined when unrecognised. */
export function mapClientStatus(config, value) {
    const key = value?.trim().toLowerCase();
    if (!key || !config.clientStatusMap)
        return undefined;
    return config.clientStatusMap[key];
}
/**
 * Feed ids that have been renamed, old → current.
 *
 * Client rows in DynamoDB store feed ids as plain strings, and job history
 * notes quote the id they were ingested under. Renaming a config would leave
 * both dangling — a stored `kerfoot-cer-export` would resolve to nothing and
 * that client's uploads would fail as `unknown-client`. Resolving through this
 * map keeps already-stored rows working while `seedClients` rewrites them.
 */
export const LEGACY_FEED_CONFIG_IDS = {
    'kerfoot-cer-export': 'cer-portal-export',
};
/** Current id for a feed config, following any rename. Unknown ids pass through. */
export function canonicalFeedConfigId(feedConfigId) {
    return LEGACY_FEED_CONFIG_IDS[feedConfigId] ?? feedConfigId;
}
/** Resolve a config by clientId (the intake route's explicit-client path). */
export function findFeedConfig(feedConfigId) {
    const id = canonicalFeedConfigId(feedConfigId);
    return CLIENT_FEED_CONFIGS.find((c) => c.feedConfigId === id);
}
/**
 * Auto-detect which feed a CSV belongs to from its header row. A config
 * matches when EVERY header in its signature is present (trimmed,
 * case-insensitive). Ties break towards the longer (more specific)
 * signature, so `generic` only wins when nothing better matches.
 */
export function feedMatchesHeaders(config, headers) {
    const have = new Set(headers.map((h) => h.trim().toLowerCase()));
    return config.match.headerSignature.every((sig) => have.has(sig.trim().toLowerCase()));
}
export function detectFeedConfig(headers) {
    const matches = CLIENT_FEED_CONFIGS.filter((c) => feedMatchesHeaders(c, headers));
    matches.sort((a, b) => b.match.headerSignature.length - a.match.headerSignature.length);
    return matches[0];
}
//# sourceMappingURL=client-feeds.js.map